import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApiError, inboxApi,
  type AssignmentRule, type RoutingFlow, type TenantSettings,
} from '@wa/shared';

/**
 * What happens to the next message that arrives.
 *
 * The screen this replaces was two lists — Rules and Groups — with no statement
 * anywhere of the order things happen in, which is the only question anybody has.
 * It could not answer "why did this chat go to nobody?", and it did not mention
 * the bot at all, even though a flow runs before any rule is consulted.
 *
 * So the screen is the journey, in order, numbered. Each step says what it does,
 * what it will do given the current configuration, and — where it can — what is
 * wrong with that. The rules and groups editors are unchanged underneath; they
 * are steps 2 and 3 rather than the whole page.
 */
export function RoutingJourney({ children }: { children: React.ReactNode }) {
  const settings = useQuery({ queryKey: ['settings'], queryFn: inboxApi.settings });
  const flows = useQuery({ queryKey: ['routing-flows'], queryFn: inboxApi.routingFlows });
  const rules = useQuery({ queryKey: ['assignment-rules'], queryFn: inboxApi.assignmentRules });

  const live = (flows.data ?? []).filter((f) => f.isEnabled);
  const activeRules = (rules.data ?? []).filter((r) => r.isActive);
  const botFirst = settings.data?.botOwnsUntilHandoff ?? true;

  return (
    // Two columns on a wide screen, summary first in the DOM.
    //
    // One column left half of a 1920 screen empty for a page that is a sequence
    // *plus* a running summary of it — and put the summary at the top, where it
    // scrolls away exactly when you start editing the rules it describes. It is
    // sticky beside them now: change a rule, watch the sentence change.
    //
    // `flex-row-reverse` rather than ordering classes, so the summary stays first
    // for a screen reader and for anyone on a phone, which is where it belongs.
    //
    // Container-query breakpoints, not viewport ones. This panel used to fill the
    // viewport; it now sits beside a 288px settings column, and `lg:` fired at a
    // 1024px viewport with 680px actually available — the rules list rendered
    // 22px wide. `@4xl` is the settings pane itself being 896px or wider.
    <div className="flex flex-col gap-4 @4xl:flex-row-reverse @4xl:items-start @4xl:gap-5">
      <aside className="@4xl:sticky @4xl:top-4 @4xl:w-80 @4xl:shrink-0">
        <Summary
          settings={settings.data}
          flows={live}
          rules={activeRules}
          loading={settings.isPending || flows.isPending || rules.isPending}
        />
      </aside>

      {/* The steps, on a spine. Four separate cards read as four settings; a line
          down the side reads as one sequence, which is what they are. */}
      <ol className="relative flex min-w-0 flex-1 flex-col gap-3">
        <span
          aria-hidden="true"
          className="absolute top-6 bottom-6 left-[15px] hidden w-px bg-(--color-line) sm:block"
        />

      <Step
        n={1}
        title="A bot answers first"
        blurb="If a flow is switched on for the number the message arrived at, it runs before anything else."
      >
        <BotStep flows={flows.data ?? []} settings={settings.data} botFirst={botFirst} />
      </Step>

      <Step
        n={2}
        title="A rule decides which team"
        blurb={botFirst
          ? 'Checked when the bot hands over, when a flow ends, and straight away on numbers with no flow.'
          : 'Checked on every first message, at the same time as the bot.'}
      >
        {children}
      </Step>

      <Step
        n={3}
        title="The least busy person in that team gets it"
        blurb="How it picks is fixed. Who it may pick from is yours."
      >
        <SelectionStep settings={settings.data} />
      </Step>

      <Step
        n={4}
        title="The customer is told who has it"
        blurb="Off by default. Outside the 24-hour window each notice is a paid conversation."
      >
        <NoticeStep settings={settings.data} />
      </Step>
      </ol>
    </div>
  );
}

/* ------------------------------------------------------------------ summary -- */

/**
 * One paragraph saying what will actually happen, assembled from the live
 * configuration rather than describing the feature in the abstract.
 *
 * This is the part that answers "why did that chat go to nobody?" without anyone
 * having to read four sections and hold them in their head at once.
 */
function Summary({ settings, flows, rules, loading }: {
  settings?: TenantSettings;
  flows: RoutingFlow[];
  rules: AssignmentRule[];
  loading: boolean;
}) {
  const problems = useMemo(() => {
    if (!settings) return [];
    const out: string[] = [];

    if (rules.length === 0) {
      out.push('No rule is switched on, so nothing is assigned automatically — every conversation waits in Unassigned.');
    } else if (!rules.some((r) => !r.matchChannelId && !r.matchTagId && !r.matchKeyword)) {
      out.push('No “everything else” rule. A conversation matching none of the rules above stays unassigned.');
    }

    // The one that is invisible from anywhere else on this screen.
    const stuck = flows.filter((f) => !f.hasHandoff);
    if (settings.botOwnsUntilHandoff && stuck.length > 0) {
      out.push(`${stuck.map((f) => `“${f.name}”`).join(', ')} never hands over to a person, and the bot goes first — so conversations on that number are never assigned by a rule.`);
    }

    if (settings.assignmentNoticeMode !== 0 && !settings.assignmentNoticeTemplateId) {
      out.push('No template is nominated for the notice, so nothing is sent once the 24-hour window has closed.');
    }

    return out;
  }, [settings, flows, rules]);

  if (loading) {
    return <p className="rounded-lg border border-(--color-line) bg-(--color-surface) px-4 py-6 text-small text-(--color-muted)">Reading your setup…</p>;
  }

  return (
    <section className="rounded-lg border border-(--color-line) bg-(--color-surface) px-3.5 py-3">
      <h2 className="text-small font-semibold">What happens to the next message</h2>

      <ol className="mt-2 flex flex-col gap-1.5 text-small">
        <Line n="1">
          {flows.length === 0
            ? 'No bot is switched on, so nothing answers automatically.'
            : `${flows.map((f) => `“${f.name}”`).join(', ')} answers on ${flows.some((f) => f.channelId === null) ? 'every number' : 'the numbers it is set up for'}.`}
        </Line>
        <Line n="2">
          {rules.length === 0
            ? 'No rule is on, so nobody is chosen.'
            : `${rules.length} rule${rules.length === 1 ? '' : 's'}, checked top to bottom — the first one that matches wins.`}
        </Line>
        <Line n="3">The person in that team with the fewest open chats, longest since their last one.</Line>
        <Line n="4">
          {settings?.assignmentNoticeMode === 0
            ? 'The customer is told nothing.'
            : settings?.assignmentNoticeMode === 1
              ? 'The customer is told the first time a person takes it.'
              : 'The customer is told every time the person changes.'}
        </Line>
      </ol>

      {problems.length > 0 && (
        <ul className="mt-3 flex flex-col gap-1.5 border-t border-(--color-line) pt-2.5">
          {problems.map((p) => (
            <li key={p} className="flex gap-2 text-small text-(--color-warn)">
              <span aria-hidden="true" className="shrink-0">!</span>
              <span className="min-w-0">{p}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Line({ n, children }: { n: string; children: React.ReactNode }) {
  return (
    <li className="flex gap-2.5">
      <span aria-hidden="true" className="grid size-5 shrink-0 place-items-center rounded-full bg-(--color-sunken) text-micro font-semibold text-(--color-muted)">
        {n}
      </span>
      <span className="min-w-0">{children}</span>
    </li>
  );
}

function Step({ n, title, blurb, children }: {
  n: number; title: string; blurb: string; children: React.ReactNode;
}) {
  return (
    <li className="relative sm:pl-10">
      {/* The badge sits on the spine rather than inside the card, so the sequence
          is visible before any of the cards are read. */}
      <span
        aria-hidden="true"
        className="absolute top-3 left-0 hidden size-8 place-items-center rounded-full border border-(--color-line) bg-(--color-surface) text-small font-semibold text-(--color-brand) sm:grid"
      >
        {n}
      </span>

      <section className="rounded-lg border border-(--color-line) bg-(--color-surface)">
        <header className="flex items-start gap-3 px-3 pt-2.5 pb-2 sm:px-4">
          <span aria-hidden="true" className="grid size-5 shrink-0 place-items-center rounded-full bg-(--color-brand-wash) text-micro font-semibold text-(--color-brand) sm:hidden">
            {n}
          </span>
          <div className="min-w-0">
            <h2 className="text-body font-semibold">{title}</h2>
            <p className="mt-0.5 text-small text-(--color-muted)">{blurb}</p>
          </div>
        </header>
        <div className="border-t border-(--color-line) px-3 py-2.5 sm:px-4">{children}</div>
      </section>
    </li>
  );
}

/* ---------------------------------------------------------------- step one -- */

function BotStep({ flows, settings, botFirst }: {
  flows: RoutingFlow[]; settings?: TenantSettings; botFirst: boolean;
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (next: boolean) => inboxApi.updateSettings({ botOwnsUntilHandoff: next }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['settings'] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : 'That did not save.'),
  });

  return (
    <div className="flex flex-col gap-3">
      {flows.length === 0 ? (
        <p className="text-small text-(--color-muted)">
          No flows exist for this account. Every message goes straight to step 2.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {flows.map((flow) => (
            <li key={flow.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-(--radius-field) border border-(--color-line) px-2.5 py-2">
              <span className="min-w-0 flex-1">
                <span className="block truncate text-small font-medium">{flow.name}</span>
                <span className="block truncate text-micro text-(--color-muted)">
                  {flow.channelName ?? (flow.channelId === null ? 'Every number' : 'One number')}
                </span>
              </span>
              <span className={[
                'shrink-0 rounded-(--radius-chip) px-2 py-0.5 text-micro font-medium',
                flow.isEnabled
                  ? 'bg-(--color-brand-wash) text-(--color-brand)'
                  : 'bg-(--color-sunken) text-(--color-muted)',
              ].join(' ')}>
                {flow.isEnabled ? 'On' : 'Off'}
              </span>
              {flow.isEnabled && !flow.hasHandoff && (
                <p className="w-full text-micro text-(--color-warn)">
                  This flow has no step that hands over to a person.
                </p>
              )}
            </li>
          ))}
        </ul>
      )}

      <label className="flex items-start gap-2.5 rounded-(--radius-field) bg-(--color-sunken) px-2.5 py-2">
        <input
          type="checkbox"
          checked={botFirst}
          disabled={save.isPending || !settings}
          onChange={(event) => save.mutate(event.target.checked)}
          className="mt-0.5 size-4 shrink-0 accent-(--color-brand)"
        />
        <span className="min-w-0 text-small">
          <span className="block font-medium">Let the bot finish before a rule picks anybody</span>
          {/* The reason, not the restatement. Somebody turning this off should know
              what they are turning on. */}
          <span className="mt-0.5 block text-micro text-(--color-muted)">
            With this off, a rule assigns an agent on the very first message — so the
            agent watches the bot talk to their customer, and the bot&rsquo;s own
            hand-over step does nothing, because the chat already has an owner.
          </span>
        </span>
      </label>

      {error && <p role="alert" className="text-micro text-(--color-bad)">{error}</p>}
    </div>
  );
}

/* -------------------------------------------------------------- step three -- */

function SelectionStep({ settings }: { settings?: TenantSettings }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (assignmentPool: 0 | 1 | 2) => inboxApi.updateSettings({ assignmentPool }),
    onSuccess: () => { setError(null); void queryClient.invalidateQueries({ queryKey: ['settings'] }); },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'That did not save.'),
  });

  return (
    <>
    <ol className="flex flex-col gap-2 text-small">
      {[
        ['Fewest open chats', 'Resolved chats do not count, so somebody who closes their work gets the next one.'],
        ['Then longest since their last', 'This is the round-robin: two agents on three chats each go in turn rather than the same one every time.'],
        ['Then by who joined first', 'A tiebreak that never changes, so the order is predictable rather than random.'],
      ].map(([title, why]) => (
        <li key={title} className="flex gap-2.5">
          <span aria-hidden="true" className="mt-1.5 size-1.5 shrink-0 rounded-full bg-(--color-brand)" />
          <span className="min-w-0">
            <span className="font-medium">{title}</span>
            <span className="block text-micro text-(--color-muted)">{why}</span>
          </span>
        </li>
      ))}
    </ol>

    <fieldset className="mt-3 border-t border-(--color-line) pt-3">
      <legend className="text-small font-medium">Who it may pick from</legend>
      <p className="mt-0.5 text-micro text-(--color-muted)">
        Always: active people in the rule&rsquo;s team who are allowed on that number.
        The question is what the <strong>Available / Away</strong> switch does.
      </p>

      <div className="mt-2 flex flex-col gap-1.5">
        {([
          [0, 'Only people who are Available',
            'Nobody Available means nobody is chosen and the chat waits in Unassigned, where the whole team can see it.'],
          [1, 'Available first, then anyone',
            'Prefers people at their desk. If none are, it still picks the least busy rather than leaving the chat in the queue.'],
          [2, 'Anyone, ignore Available entirely',
            'For a team that does not use the switch. Every chat gets an owner, including people who have logged off.'],
        ] as const).map(([value, label, why]) => (
          <label key={value} className="flex items-start gap-2.5">
            <input
              type="radio"
              name="assignment-pool"
              checked={(settings?.assignmentPool ?? 0) === value}
              disabled={save.isPending || !settings}
              onChange={() => save.mutate(value)}
              className="mt-0.5 size-4 shrink-0 accent-(--color-brand)"
            />
            <span className="min-w-0 text-small">
              <span className="block">{label}</span>
              <span className="block text-micro text-(--color-muted)">{why}</span>
            </span>
          </label>
        ))}
      </div>

      {/* The cost of the two non-default options, said once, where the choice is
          made. An assigned chat leaves the queue everybody watches — that is the
          whole trade, and it is invisible from anywhere else in the product. */}
      {(settings?.assignmentPool ?? 0) !== 0 && (
        <p className="mt-2 text-micro text-(--color-warn)">
          A chat given to somebody who is not at their desk leaves the Unassigned
          queue, so nobody else sees it waiting. Worth pairing with an SLA rule.
        </p>
      )}

      <p className="mt-2 text-micro text-(--color-muted)">
        Working hours are separate: while you are closed, <strong>Hold assignment</strong>
        {' '}under Hours decides whether routing runs at all. Closed is about the
        business; Away is about one person.
      </p>

      {error && <p role="alert" className="mt-1.5 text-micro text-(--color-bad)">{error}</p>}
    </fieldset>
    </>
  );
}

/* --------------------------------------------------------------- step four -- */

function NoticeStep({ settings }: { settings?: TenantSettings }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [text, setText] = useState<string | null>(null);
  const [noAgentText, setNoAgentText] = useState<string | null>(null);

  const templates = useQuery({
    queryKey: ['templates'],
    queryFn: () => inboxApi.templates(),
    staleTime: 300_000,
  });

  const save = useMutation({
    mutationFn: (patch: Partial<TenantSettings>) => inboxApi.updateSettings(patch),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'That did not save.'),
  });

  if (!settings) return <p className="text-small text-(--color-muted)">Loading…</p>;

  const mode = settings.assignmentNoticeMode;
  const body = text ?? settings.assignmentNoticeText ?? '';
  const noAgent = noAgentText ?? settings.noAgentNoticeText ?? '';

  // One variable only — the agent's name goes in it. The same shape the session
  // opener requires, and for the same reason: anything else cannot be filled.
  const usable = (templates.data ?? []).filter(
    (t) => t.status === 'APPROVED' && t.bodyVariables === 1);

  return (
    <div className="flex flex-col gap-4">
      <fieldset>
        <legend className="text-small font-medium">When to tell them</legend>
        <div className="mt-1.5 flex flex-col gap-1.5">
          {([
            [0, 'Never', 'Nothing is sent. Nothing is charged.'],
            [1, 'The first time a person takes it', 'One message per 24-hour window. Passing the chat between agents after that is silent.'],
            [2, 'Every time the person changes', 'Three agents means three messages. Outside the window that is three paid conversations.'],
          ] as const).map(([value, label, why]) => (
            <label key={value} className="flex items-start gap-2.5">
              <input
                type="radio"
                name="notice-mode"
                checked={mode === value}
                disabled={save.isPending}
                onChange={() => save.mutate({ assignmentNoticeMode: value })}
                className="mt-0.5 size-4 shrink-0 accent-(--color-brand)"
              />
              <span className="min-w-0 text-small">
                <span className="block">{label}</span>
                <span className="block text-micro text-(--color-muted)">{why}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      {mode !== 0 && (
        <>
          <label className="block">
            <span className="text-small font-medium">Inside the 24-hour window</span>
            <span className="mt-0.5 block text-micro text-(--color-muted)">
              An ordinary message, no extra charge. Write <code>{'{{agent}}'}</code> where the name goes.
            </span>
            <textarea
              rows={2}
              value={body}
              maxLength={1000}
              onChange={(event) => setText(event.target.value)}
              onBlur={() => {
                if (body.trim() !== (settings.assignmentNoticeText ?? '')) {
                  save.mutate({ assignmentNoticeText: body.trim() });
                }
              }}
              placeholder="You are now connected with {{agent}}. They will help you from here."
              className="tap mt-1.5 w-full resize-y rounded-(--radius-field) border border-(--color-line) bg-(--color-surface) px-2.5 py-2 text-small outline-none focus:border-(--color-brand)"
            />
          </label>

          <label className="block">
            <span className="text-small font-medium">Outside it</span>
            <span className="mt-0.5 block text-micro text-(--color-muted)">
              Meta allows only an approved template here, and each one is a <strong>billed
              conversation</strong>. Leave it unset to send nothing once the window closes.
            </span>
            <select
              value={settings.assignmentNoticeTemplateId ?? ''}
              disabled={save.isPending}
              onChange={(event) => save.mutate({
                assignmentNoticeTemplateId: event.target.value ? Number(event.target.value) : -1,
              })}
              className="tap mt-1.5 w-full rounded-(--radius-field) border border-(--color-line) bg-(--color-surface) px-2.5 py-2 text-small outline-none focus:border-(--color-brand)"
            >
              <option value="">Send nothing outside the window</option>
              {usable.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            {usable.length === 0 && (
              <span className="mt-1 block text-micro text-(--color-muted)">
                No approved template takes exactly one variable, which is what this needs.
              </span>
            )}
          </label>
        </>
      )}

      <fieldset className="border-t border-(--color-line) pt-3">
        <legend className="text-small font-medium">When nobody is available</legend>
        <span className="mt-0.5 block text-micro text-(--color-muted)">
          The bot has handed over and everyone is Away or it is out of hours.
        </span>
        <div className="mt-1.5 flex flex-col gap-1.5">
          {([
            [0, 'Say nothing'],
            [1, 'Send a message of your own'],
            [2, 'Send the working-hours away message'],
          ] as const).map(([value, label]) => (
            <label key={value} className="flex items-center gap-2.5">
              <input
                type="radio"
                name="no-agent-mode"
                checked={settings.noAgentNoticeMode === value}
                disabled={save.isPending || (value === 2 && !settings.awayMessage)}
                onChange={() => save.mutate({ noAgentNoticeMode: value })}
                className="size-4 shrink-0 accent-(--color-brand)"
              />
              <span className="text-small">
                {label}
                {value === 2 && !settings.awayMessage && (
                  <span className="text-(--color-muted)"> — none is set under Hours</span>
                )}
              </span>
            </label>
          ))}
        </div>

        {settings.noAgentNoticeMode === 1 && (
          <textarea
            rows={2}
            value={noAgent}
            maxLength={1000}
            onChange={(event) => setNoAgentText(event.target.value)}
            onBlur={() => {
              if (noAgent.trim() !== (settings.noAgentNoticeText ?? '')) {
                save.mutate({ noAgentNoticeText: noAgent.trim() });
              }
            }}
            placeholder="Thanks — your request is with our team and somebody will reply shortly."
            className="tap mt-2 w-full resize-y rounded-(--radius-field) border border-(--color-line) bg-(--color-surface) px-2.5 py-2 text-small outline-none focus:border-(--color-brand)"
          />
        )}
      </fieldset>

      {error && <p role="alert" className="text-micro text-(--color-bad)">{error}</p>}
    </div>
  );
}
